// src/hooks/useStudentModernData.js
//
// Pass 85.1A — MODERN, ORG+PARTICIPANT scoped student reads. Identity is the canonical participantId (linked to THIS
// org), never the raw Auth uid, so Org A never blends with Org B and the 75 legacy tenantless videos are structurally
// excluded. Every snapshot is scope-keyed `uid::org::participantId` and suppressed synchronously at render when the
// scope changes (mirrors the instructor hooks), and every hook exposes DISTINCT { loading, error, items } so a read
// failure is surfaced as an error — never rendered as an empty "no videos / no lessons".

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { loadParticipantLessonProjections } from '../utils/studentDataAccess';

const scopeKeyOf = (uid, org, pid) => (uid && org && pid ? `${uid}::${org}::${pid}` : '');

// Live modern videos for a participant in one org: `videos where organizationId==org AND participantId==pid`.
export function useStudentModernVideos(authUid, organizationId, participantId) {
  const [snap, setSnap] = useState({ scopeKey: '', items: [], loading: true, error: null });
  const requestedScopeKey = scopeKeyOf(authUid, organizationId, participantId);
  useEffect(() => {
    setSnap({ scopeKey: requestedScopeKey, items: [], loading: !!requestedScopeKey, error: null });
    if (!requestedScopeKey) return undefined; // fail closed — no identity-only / cross-org read
    const q = query(collection(db, 'videos'), where('organizationId', '==', organizationId), where('participantId', '==', participantId));
    const unsub = onSnapshot(
      q,
      (s) => setSnap({ scopeKey: requestedScopeKey, items: s.docs.map((d) => ({ id: d.id, ...d.data() })), loading: false, error: null }),
      (err) => setSnap({ scopeKey: requestedScopeKey, items: [], loading: false, error: err || new Error('student_videos_read_failed') }),
    );
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [authUid, organizationId, participantId, requestedScopeKey]);
  return useMemo(() => {
    if (snap.scopeKey !== requestedScopeKey) return { loading: !!requestedScopeKey, error: null, videos: [] };
    return { loading: snap.loading, error: snap.error, videos: snap.items };
  }, [snap, requestedScopeKey]);
}

// Modern lesson participation for a participant in one org → the SAFE published PROJECTIONS only (unpublished lessons
// are simply absent). Two-step read (links → projections) via the read-only loader; distinct loading/error states.
export function useStudentLessonProjections(authUid, organizationId, participantId) {
  const [snap, setSnap] = useState({ scopeKey: '', items: [], loading: true, error: null });
  const requestedScopeKey = scopeKeyOf(authUid, organizationId, participantId);
  useEffect(() => {
    let cancelled = false;
    setSnap({ scopeKey: requestedScopeKey, items: [], loading: !!requestedScopeKey, error: null });
    if (!requestedScopeKey) return undefined;
    loadParticipantLessonProjections(db, { participantId, organizationId })
      .then((items) => { if (!cancelled) setSnap({ scopeKey: requestedScopeKey, items, loading: false, error: null }); })
      .catch((err) => { if (!cancelled) setSnap({ scopeKey: requestedScopeKey, items: [], loading: false, error: err || new Error('student_lessons_read_failed') }); });
    return () => { cancelled = true; };
  }, [authUid, organizationId, participantId, requestedScopeKey]);
  return useMemo(() => {
    if (snap.scopeKey !== requestedScopeKey) return { loading: !!requestedScopeKey, error: null, lessons: [] };
    return { loading: snap.loading, error: snap.error, lessons: snap.items };
  }, [snap, requestedScopeKey]);
}

export default useStudentModernVideos;
